import { useAppSelector } from "@/store/hooks";
import { resolveSlideTemplate } from "@ai-teach/types";
import { useGrammarHandlers } from "./grammar/useGrammarHandlers";
import { useGrammarResponseHandlers } from "./grammar/useGrammarResponseHandlers";
import { useHandWritingHandlers } from "./handWriting/useHandWritingHandlers";
import { useFreeformHandlers } from "./other/useFreeformHandlers";
import { usePhonicsHandlers } from "./phonics/usePhonicsHandlers";
import { useReadingHandlers } from "./reading/useReadingHandlers";
import type { SlideHandlers } from "./types";
import { useWordHandlers } from "./word/useWordHandlers";
import { useWordVocabularyHandlers } from "./word/useWordVocabularyHandlers";

export const useSlideHandlers = (): SlideHandlers => {
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const slideType = slide?.type;
  const knowledgeArea = slide?.knowledgeArea;

  const wordHandlers = useWordHandlers(slide);
  const wordVocabularyHandlers = useWordVocabularyHandlers(slide);
  const grammarHandlers = useGrammarHandlers(slide);
  const grammarResponseHandlers = useGrammarResponseHandlers(slide);
  const readingHandlers = useReadingHandlers(slide);
  const handWritingHandlers = useHandWritingHandlers(slide);
  const phonicsHandlers = usePhonicsHandlers();
  const freefromHandlers = useFreeformHandlers();

  const handlersMap: Record<string, Partial<Record<string, SlideHandlers>>> = {
    word: {
      pronunciation: wordHandlers,
      usage: wordHandlers,
      meaning: freefromHandlers,
      vocabulary: wordVocabularyHandlers,
    },
    grammar: {
      form: grammarHandlers,
      meaning: grammarHandlers,
      usage: grammarHandlers,
      sentence: grammarHandlers,
      response: grammarResponseHandlers,
    },
    reading: {
      fluency: readingHandlers,
      comprehension: readingHandlers,
    },
    phonics: {
      encode: phonicsHandlers,
      decode: phonicsHandlers,
    },
    other: {
      reading: freefromHandlers,
      writing: freefromHandlers,
      listening: freefromHandlers,
      speaking: freefromHandlers,
    },
  };

  // Templates that share a legacy pair with freeform slides must be matched on the template
  // itself — old freeform data is also stored as other/writing.
  if (resolveSlideTemplate(slide) === "handWriting") return handWritingHandlers;

  // Pick handler by type + knowledgeArea
  const handler = slideType && knowledgeArea ? handlersMap[slideType]?.[knowledgeArea] : undefined;

  // Fallback to grammarHandlers if missing
  return handler ?? grammarHandlers;
};
